import { useSearchParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Search, Inbox } from 'lucide-react';
import { useState, useEffect } from 'react';
import { searchArticles, getErrorCode } from '@/services/api';
import { Breadcrumbs, SearchResultRow } from '@/components';
import { ErrorState, EmptyState, LoadingSpinner } from '@airclou/ui';

export function SearchPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const initialQuery = searchParams.get('q') || '';
  const mode = (searchParams.get('mode') as 'keyword' | 'semantic' | 'hybrid') || 'hybrid';
  const [searchQuery, setSearchQuery] = useState(initialQuery);

  const {
    data: results,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['search', initialQuery, mode],
    queryFn: () => searchArticles(initialQuery, mode),
    enabled: !!initialQuery,
  });

  useEffect(() => {
    setSearchQuery(initialQuery);
  }, [initialQuery]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim() && searchQuery !== initialQuery) {
      navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
    }
  };

  return (
    <div className="mx-auto max-w-[1120px] px-4 md:px-6 py-8">
      <Breadcrumbs
        items={[
          { label: 'Home', href: '/' },
          { label: 'Search' },
        ]}
      />

      {/* Search Input */}
      <form onSubmit={handleSearch} className="mb-8">
        <div className="relative max-w-2xl">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
          <input
            type="search"
            placeholder="Search for answers..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full h-12 pl-12 pr-4 rounded-lg border bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>
      </form>

      {/* Results */}
      {initialQuery ? (
        <>
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <LoadingSpinner />
            </div>
          ) : error ? (
            <ErrorState
              description={
                getErrorCode(error) === 'RATE_LIMITED'
                  ? 'Too many searches. Please wait a moment and try again.'
                  : 'Search failed. Please try again.'
              }
              action={
                <button
                  onClick={() => refetch()}
                  className="text-primary hover:underline"
                >
                  Try again
                </button>
              }
            />
          ) : results && results.length > 0 ? (
            <div>
              <p className="text-sm text-muted-foreground mb-4">
                {results.length} {results.length === 1 ? 'result' : 'results'} for "{initialQuery}"
              </p>
              <div className="space-y-3">
                {results.map((result) => (
                  <SearchResultRow
                    key={result.id}
                    result={result}
                    query={initialQuery}
                  />
                ))}
              </div>
            </div>
          ) : (
            <EmptyState
              icon={Inbox}
              title="No results found"
              description={`We couldn't find any articles matching "${initialQuery}". Try checking your spelling, using different keywords, or browsing our categories.`}
            />
          )}
        </>
      ) : (
        <p className="text-muted-foreground">Enter a search term to find articles.</p>
      )}
    </div>
  );
}
